import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import useToggle from "../../Hooks/useToggle";

import navigationContext from "../../contexts/navigationsContext";
import { navigations } from "../../Data/navigation.json";
import HeaderOne from "../Partials/Headers/HeaderOne";
import Footer from "../Partials/Footers/HomeOne/Footer";
import Drawer from "../Mobile/Drawer";
import BackToTop from "../Helpers/BackToTop";

import { getProductDetail } from "../../services/HomeServices";
import { addToCart } from "../../services/AuthServices";
import { getUserId, isLogin } from "../../lib/constant";
import fallbackImg from "../../assets/images/no-image-found.jpg";

import "./ProductDetail.css";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationData = navigations;
  const [drawer, setDrawer] = useToggle(false);

  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [qty, setQty] = useState(0);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setIsLoading(true);

    // First, try to get product from navigation state (passed from homepage)
    const stateProduct = location.state?.product;

    if (stateProduct && (stateProduct.id || stateProduct.product_id)) {
      // Use product data passed from homepage
      setProduct(stateProduct);
      setQty(stateProduct?.qty || 0);
      setIsLoading(false);
      return;
    }

    // Fallback: Try to fetch from API if no state data
    try {
      if (!id || id === "undefined" || id === "null") {
        console.error("Invalid product ID:", id);
        setProduct(null);
        setIsLoading(false);
        return;
      }

      const response = await getProductDetail(id);
      const productData = response?.data?.data || response?.data?.product || response?.data;

      if (productData && (productData.id || productData.product_id)) {
        setProduct(productData);
        setQty(productData?.qty || 0);
      } else {
        console.error("Invalid product data received:", response?.data);
        setProduct(null);
      }
    } catch (error) {
      console.error("Error fetching product detail:", error);
      // If API fails and no state data, product will be null
      setProduct(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQtyChange = (newQty) => {
    if (newQty < 0) return;
    if (product?.stock && newQty > product.stock) return;
    setQty(newQty);
  };

  const handleAddToCart = async () => {
    if (!isLogin()) {
      toast("Please login to add items to cart", { type: "warning" });
      navigate("/login");
      return;
    }

    try {
      const formdata = new FormData();
      const productId = product.id || product.product_id;
      formdata.append("product_id", productId);
      formdata.append("quantity", qty);

      await addToCart(formdata);
      toast("Product added to cart successfully", { type: "success" });
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong";
      toast(message, { type: "error" });
    }
  };

  const calculateDiscount = () => {
    if (!product?.base_price || !product?.discount_price) return 0;
    if (product.base_price <= product.discount_price) return 0;
    return Math.round(((product.base_price - product.discount_price) / product.base_price) * 100);
  };

  const discount = calculateDiscount();
  const finalPrice = product?.discount_price || product?.base_price || 0;
  const inStock = product?.stock > 0;

  /* ── render states ── */
  if (isLoading) {
    return (
      <div className="pd-page">
        <navigationContext.Provider value={{ navigationData }}>
          <HeaderOne drawerAction={setDrawer.toggle} />
        </navigationContext.Provider>
        <div className="pd-center">
          <div className="pd-spinner"></div>
          <p>Loading product…</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd-page">
        <navigationContext.Provider value={{ navigationData }}>
          <HeaderOne drawerAction={setDrawer.toggle} />
        </navigationContext.Provider>
        <div className="pd-center">
          <h3>Product not found</h3>
          <p>The product you are looking for doesn't exist or has been removed.</p>
          <button className="pd-back-btn" style={{ marginTop: 16 }} onClick={() => navigate("/")}>
            <i className="fas fa-arrow-left"></i> Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="pd-page"
    >
      <Drawer drawer={drawer} action={setDrawer.toggle} navigationData={navigationData} />

      <navigationContext.Provider value={{ navigationData }}>
        <HeaderOne drawerAction={setDrawer.toggle} />
      </navigationContext.Provider>

      {/* Breadcrumb */}
      <div className="pd-breadcrumb">
        <div className="pd-breadcrumb-inner">
          <a href="#/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>Home</a>
          <span className="pd-bc-sep">›</span>
          <a href="#/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>Products</a>
          <span className="pd-bc-sep">›</span>
          <span className="pd-bc-current">{product.name}</span>
        </div>
      </div>

      {/* Main content */}
      <div className="pd-container">
        <button className="pd-back-btn" onClick={() => navigate("/")}>
          <i className="fas fa-arrow-left"></i> Back to Products
        </button>

        <div className="pd-grid">
          {/* Left — image */}
          <div className="pd-image-section">
            <div className="pd-main-image">
              <img
                src={product.image || fallbackImg}
                alt={product.name}
                onError={(e) => { e.target.src = fallbackImg; }}
              />
            </div>
          </div>

          {/* Right — details */}
          <div className="pd-details">
            <h1 className="pd-title">{product.name}</h1>

            {/* Pricing */}
            <div className="pd-pricing">
              <span className="pd-price-current">₹{finalPrice}</span>
              {product.base_price && product.base_price !== product.discount_price && (
                <span className="pd-price-original">₹{product.base_price}</span>
              )}
              {discount > 0 && (
                <span className="pd-price-save">You save ₹{product.base_price - finalPrice}</span>
              )}
            </div>

            <div className="pd-divider"></div>

            {/* Stock & meta */}
            <div className="pd-meta">
              <div className="pd-meta-item">
                <i className="fas fa-box"></i>
                <span>Stock: <strong className={inStock ? "pd-stock-in" : "pd-stock-out"}>
                  {inStock ? `${product.stock} available` : "Out of stock"}
                </strong></span>
              </div>
              {(product.id || product.product_id) && (
                <div className="pd-meta-item">
                  <i className="fas fa-hashtag"></i>
                  <span>ID: <strong>{product.id || product.product_id}</strong></span>
                </div>
              )}
            </div>

            <div className="pd-divider"></div>

            {/* Description */}
            <p className="pd-label">Description</p>
            <p className="pd-description">
              {product.description || "No description available for this product."}
            </p>

            <div className="pd-divider"></div>

            {/* Quantity */}
            <div className="pd-qty-row">
              <span className="pd-qty-label">Quantity</span>
              <div className="pd-qty-controls">
                <button className="pd-qty-btn" onClick={() => handleQtyChange(qty - 1)} disabled={qty <= 0}>
                  −
                </button>
                <span className="pd-qty-value">{qty}</span>
                <button className="pd-qty-btn" onClick={() => handleQtyChange(qty + 1)} disabled={!inStock || qty >= product.stock}>
                  +
                </button>
              </div>
            </div>

            {/* Add to cart */}
            <button
              className="pd-add-btn"
              onClick={handleAddToCart}
              disabled={!inStock || qty <= 0}
            >
              <i className="fas fa-shopping-cart"></i>
              {qty === 0 ? "Select Quantity" : `Add ${qty} to Cart`}
            </button>
          </div>
        </div>
      </div>

      <Footer />
      <BackToTop />
    </motion.div>
  );
};

export default ProductDetail;
